import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  Navbar,
  NavbarBrand,
  UncontrolledCollapse,
  Input,
  Button,
  FormGroup,
  Label,
  FormFeedback,
  CustomInput,
  Spinner,
} from 'reactstrap';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { customDropdownWithBorder } from 'util/reactCustomStyles';
import useCustomPmrPrograms from 'util/useCustomPmrPrograms';
import { getPlansForSpecialty } from 'config/subscriptionPlans';
import { SPECIALTIES, DEFAULT_SPECIALTY, getSpecialty } from 'config/specialtyConfig';

import 'assets/css/pmr-landing.css';

const specialtyOptions = Object.values(SPECIALTIES).map((s) => ({
  value: s.id,
  label: s.fullName,
}));

const roleOptions = [
  { value: 'Current Resident', label: 'Current Resident' },
  { value: 'Program Director', label: 'Program Director' },
  { value: 'Program Coordinator', label: 'Program Coordinator' },
];

const yearOptions = [
  { value: 'Fellow', label: 'Fellow' },
  { value: 'PGY 1', label: 'PGY 1' },
  { value: 'PGY 2', label: 'PGY 2' },
  { value: 'PGY 3', label: 'PGY 3' },
  { value: 'PGY 4', label: 'PGY 4' },
];

const paymentTypeOptions = [
  { value: 'coordinator', label: 'Coordinator' },
  { value: 'individual', label: 'Individual' },
];

const GroupPricing = () => {
  const allPmrProgramOptions = useCustomPmrPrograms();
  const [navbarScrolled, setNavbarScrolled] = useState(false);

  // Form state
  const [programName, setProgramName] = useState(null);
  const [role, setRole] = useState(null);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([{ plan: null, quantity: '' }]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [paymentTypePreference, setPaymentTypePreference] = useState(null);
  const [isEntireClass, setIsEntireClass] = useState(false);
  const [isEntireProgram, setIsEntireProgram] = useState(false);
  const [specialty, setSpecialty] = useState(specialtyOptions.find((o) => o.value === DEFAULT_SPECIALTY));
  const [errors, setErrors] = useState({});

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setNavbarScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Plan options — show standard price, filtered by selected specialty
  const specialtyPlans = getPlansForSpecialty(specialty?.value || 'pmr');
  const planOptions = specialtyPlans.map((p) => ({
    value: p.id,
    label: `${p.name} — $${p.price}`,
    plan: p,
  }));

  const clearError = (field) => setErrors((prev) => ({ ...prev, [field]: '' }));

  // Handle "entire program" checkbox — auto-checks and disables "entire class"
  const handleEntireProgramChange = (checked) => {
    setIsEntireProgram(checked);
    if (checked) setIsEntireClass(true);
  };

  // Item management
  const addItem = () => setItems((prev) => [...prev, { plan: null, quantity: '' }]);
  const removeItem = (idx) => {
    setItems((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
    setErrors((prev) => ({ ...prev, items: undefined }));
  };
  const updateItem = (idx, field, value) => {
    setItems((prev) => prev.map((item, i) => i !== idx ? item : { ...item, [field]: value }));
    if (errors.items) {
      setErrors((prev) => {
        const newItemErrors = [...(prev.items || [])];
        if (newItemErrors[idx]) newItemErrors[idx] = { ...newItemErrors[idx], [field]: undefined };
        const hasAny = newItemErrors.some((ie) => ie && (ie.plan || ie.quantity));
        return { ...prev, items: hasAny ? newItemErrors : undefined };
      });
    }
  };

  // Calculate totals with discount logic
  const totalSeats = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
    [items]
  );

  const discountPercent = useMemo(() => {
    if (isEntireProgram) return 25;
    if (totalSeats >= 10 || isEntireClass) return 20;
    return 0;
  }, [isEntireProgram, isEntireClass, totalSeats]);

  const discountLabel = useMemo(() => {
    if (isEntireProgram) return '25% Program Discount';
    if (totalSeats >= 10 || isEntireClass) return '20% Group Discount';
    return '';
  }, [isEntireProgram, isEntireClass, totalSeats]);

  const itemSummaries = useMemo(() => items.map((item) => {
    if (!item.plan) return null;
    const qty = Number(item.quantity) || 0;
    if (qty <= 0) return null;
    const standardPrice = item.plan.price;
    const discountedPrice = +(standardPrice * (1 - discountPercent / 100)).toFixed(2);
    return {
      plan: item.plan,
      quantity: qty,
      standardPrice,
      discountedPrice,
      subtotal: +(discountedPrice * qty).toFixed(2),
      originalSubtotal: +(standardPrice * qty).toFixed(2),
    };
  }), [items, discountPercent]);

  const totalAmount = useMemo(
    () => +itemSummaries.reduce((sum, s) => sum + (s?.subtotal || 0), 0).toFixed(2),
    [itemSummaries]
  );
  const totalOriginal = useMemo(
    () => +itemSummaries.reduce((sum, s) => sum + (s?.originalSubtotal || 0), 0).toFixed(2),
    [itemSummaries]
  );
  const totalSavings = +(totalOriginal - totalAmount).toFixed(2);

  const seatsUntilDiscount = discountPercent === 0 && !isEntireClass && !isEntireProgram ? Math.max(0, 10 - totalSeats) : 0;

  const validateForm = () => {
    const errs = {};
    if (!programName?.value?.trim()) errs.programName = 'Program name is required';
    if (!role) errs.role = 'Role is required';
    if (!contactName.trim()) errs.contactName = 'Contact name is required';
    if (!contactEmail.trim()) {
      errs.contactEmail = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      errs.contactEmail = 'Enter a valid email address';
    }
    if (!paymentTypePreference) errs.paymentTypePreference = 'Payment preference is required';
    const itemErrors = [];
    let hasItemError = false;
    items.forEach((item) => {
      const ie = {};
      if (!item.plan) { ie.plan = 'Select a plan'; hasItemError = true; }
      if (!item.quantity || Number(item.quantity) < 1) { ie.quantity = 'Min 1'; hasItemError = true; }
      itemErrors.push(ie);
    });
    if (hasItemError) errs.items = itemErrors;
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateForm();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      const publicAxios = axios.create();
      await publicAxios.post(axios.defaults.baseURL + '/submitGroupQuoteRequest', {
        programName: programName.value.trim(),
        role: role.value,
        contactName: contactName.trim(),
        contactEmail: contactEmail.trim(),
        notes: notes.trim(),
        year: selectedYear?.value || '',
        paymentTypePreference: paymentTypePreference?.value || '',
        specialty: specialty?.value || 'pmr',
        isEntireClass,
        isEntireProgram,
        items: items.map((item) => ({
          planId: item.plan.id,
          quantity: Number(item.quantity),
        })),
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Something went wrong. Please try again.';
      setErrors({ submit: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="pmr-landing">
        {/* Navigation */}
        <Navbar className={`pmr-navbar navbar-horizontal navbar-dark fixed-top ${navbarScrolled ? 'scrolled' : ''}`} expand="lg">
          <Container>
            <NavbarBrand to="/" tag={Link} className="pmr-brand">
              <img
                alt={`${getSpecialty(specialty?.value).name} Prep Logo`}
                src={require('assets/img/brand/pmr-logo.png')}
                className="pmr-nav-logo"
              />
            </NavbarBrand>
            <button className="navbar-toggler" id="pmr-navbar-collapse-gp">
              <span className="navbar-toggler-icon" />
            </button>
            <UncontrolledCollapse navbar toggler="#pmr-navbar-collapse-gp">
              <Nav className="ml-auto align-items-center" navbar>
                <NavItem>
                  <Link to="/" className="nav-link pmr-nav-link">
                    Home
                  </Link>
                </NavItem>
                <NavItem className="ml-lg-3">
                  <Link to="/auth/login" className="btn btn-sm pmr-btn-outline">
                    Login
                  </Link>
                </NavItem>
                <NavItem className="ml-lg-2">
                  <Link className="btn btn-sm pmr-btn-primary" to="/auth/register">
                    Start Free Trial
                  </Link>
                </NavItem>
              </Nav>
            </UncontrolledCollapse>
          </Container>
        </Navbar>

        {/* Hero */}
        <section style={{
          paddingTop: '140px',
          paddingBottom: '40px',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          color: '#fff',
          textAlign: 'center',
        }}>
          <Container>
            <h1 style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: '1rem', color: '#fff' }}>
              Group Pricing
            </h1>
            <p style={{ fontSize: '1.15rem', opacity: 0.85, maxWidth: '600px', margin: '0 auto' }}>
              Select plans for your residency program and submit a quote request. Our team will follow up within 1-2 business days.
            </p>
          </Container>
        </section>

        {/* Main Content */}
        <section style={{ padding: '40px 0 60px', background: '#f8f9fe' }}>
          <Container>
            {submitted ? (
              <Row className="justify-content-center">
                <Col lg="8" className="text-center" style={{ padding: '80px 20px' }}>
                  <div style={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: '#2dce89',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.5rem',
                  }}>
                    <i className="fas fa-check" style={{ fontSize: '2rem', color: '#fff' }} />
                  </div>
                  <h2 style={{ fontWeight: 700, marginBottom: '1rem' }}>Quote Request Received!</h2>
                  <p style={{ fontSize: '1.1rem', color: '#525f7f', maxWidth: 500, margin: '0 auto 1rem' }}>
                    Our team will review your request and reach out within 1-2 business days.
                  </p>
                  <p style={{ fontSize: '0.95rem', color: '#8898aa', maxWidth: 500, margin: '0 auto 2rem' }}>
                    A confirmation email has been sent to your inbox. If you don't see it, please check your spam folder or visit the <a href="/client/group-codes" style={{ color: '#5e72e4' }}>Group Orders</a> page to check your quote status.
                  </p>
                  <Link to="/" className="btn pmr-btn-primary">
                    Back to Home
                  </Link>
                </Col>
              </Row>
            ) : (
              <form onSubmit={handleSubmit}>
                <Row>
                  <Col lg="8">
                    {/* Program Information */}
                    <Card className="border mb-4">
                      <CardHeader className="bg-white py-3">
                        <h5 className="mb-0">
                          <i className="fas fa-building mr-2 text-primary" />
                          Program Information
                        </h5>
                      </CardHeader>
                      <CardBody>
                        <FormGroup>
                          <Label className="font-weight-bold">Program Name <span className="text-danger">*</span></Label>
                          <CreatableSelect
                            options={allPmrProgramOptions}
                            value={programName}
                            onChange={(val) => { setProgramName(val); clearError('programName'); }}
                            styles={{
                              ...customDropdownWithBorder,
                              control: (base, state) => ({
                                ...customDropdownWithBorder.control(base, state),
                                ...(errors.programName ? { borderColor: '#fb6340', '&:hover': { borderColor: '#fb6340' } } : {}),
                              }),
                            }}
                            placeholder="Search or type a program name..."
                            isClearable
                            formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
                          />
                          {errors.programName && <small className="text-danger">{errors.programName}</small>}
                        </FormGroup>
                        <Row>
                          <Col md="6">
                            <FormGroup>
                              <Label className="font-weight-bold">Role <span className="text-danger">*</span></Label>
                              <Select
                                options={roleOptions}
                                value={role}
                                onChange={(val) => { setRole(val); clearError('role'); }}
                                styles={{
                                  ...customDropdownWithBorder,
                                  control: (base, state) => ({
                                    ...customDropdownWithBorder.control(base, state),
                                    ...(errors.role ? { borderColor: '#fb6340', '&:hover': { borderColor: '#fb6340' } } : {}),
                                  }),
                                }}
                                placeholder="Select your role..."
                              />
                              {errors.role && <small className="text-danger">{errors.role}</small>}
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label className="font-weight-bold">Name <span className="text-danger">*</span></Label>
                              <Input
                                value={contactName}
                                invalid={!!errors.contactName}
                                onChange={(e) => { setContactName(e.target.value); clearError('contactName'); }}
                                placeholder="Your name"
                              />
                              {errors.contactName && <FormFeedback>{errors.contactName}</FormFeedback>}
                            </FormGroup>
                          </Col>
                        </Row>
                        <FormGroup>
                          <Label className="font-weight-bold">Contact Email <span className="text-danger">*</span></Label>
                          <Input
                            type="email"
                            value={contactEmail}
                            invalid={!!errors.contactEmail}
                            onChange={(e) => { setContactEmail(e.target.value); clearError('contactEmail'); }}
                            placeholder="your.email@hospital.edu"
                          />
                          {errors.contactEmail && <FormFeedback>{errors.contactEmail}</FormFeedback>}
                        </FormGroup>
                        <FormGroup>
                          <Label className="font-weight-bold">Specialty <span className="text-danger">*</span></Label>
                          <Select
                            options={specialtyOptions}
                            value={specialty}
                            onChange={(val) => {
                              setSpecialty(val);
                              // Reset plan selections when specialty changes (planIds differ per specialty)
                              setItems([{ plan: null, quantity: '' }]);
                            }}
                            styles={customDropdownWithBorder}
                            placeholder="Select specialty..."
                          />
                        </FormGroup>
                        <Row>
                          {(!specialty || specialty.value === 'pmr') && (
                          <Col md="6">
                            <FormGroup>
                              <Label className="font-weight-bold">Year</Label>
                              <Select
                                options={yearOptions}
                                value={selectedYear}
                                onChange={(val) => setSelectedYear(val)}
                                styles={customDropdownWithBorder}
                                placeholder="Select PGY year (optional)"
                                isClearable
                              />
                            </FormGroup>
                          </Col>
                          )}
                          <Col md="6">
                            <FormGroup>
                              <Label className="font-weight-bold">Payment Preference <span className="text-danger">*</span></Label>
                              <Select
                                options={paymentTypeOptions}
                                value={paymentTypePreference}
                                onChange={(val) => { setPaymentTypePreference(val); clearError('paymentTypePreference'); }}
                                styles={{
                                  ...customDropdownWithBorder,
                                  control: (base, state) => ({
                                    ...customDropdownWithBorder.control(base, state),
                                    ...(errors.paymentTypePreference ? { borderColor: '#fb6340', '&:hover': { borderColor: '#fb6340' } } : {}),
                                  }),
                                }}
                                placeholder="Select preference"
                              />
                              {errors.paymentTypePreference ? <small className="text-danger">{errors.paymentTypePreference}</small> : <small className="text-muted">How would you prefer to handle payment?</small>}
                            </FormGroup>
                          </Col>
                        </Row>
                        <FormGroup className="mb-0">
                          <Label className="font-weight-bold">Additional Notes</Label>
                          <Input
                            type="textarea"
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Any additional information about your program or requirements..."
                          />
                        </FormGroup>
                      </CardBody>
                    </Card>

                    {/* Plans & Pricing */}
                    <Card className="border mb-4">
                      <CardHeader className="bg-white py-3">
                        <h5 className="mb-0">
                          <i className="fas fa-tags mr-2 text-success" />
                          Plans & Pricing
                        </h5>
                      </CardHeader>
                      <CardBody>
                        {/* Discount checkboxes */}
                        <div className="mb-3 p-3" style={{ background: '#f6f9fc', borderRadius: '6px' }}>
                          <CustomInput
                            type="checkbox"
                            id="isEntireClass"
                            label={(!specialty || specialty.value === 'pmr') ? "This is the entire class (e.g., all PGY-3 residents)" : "This is the entire class"}
                            checked={isEntireClass}
                            disabled={isEntireProgram}
                            onChange={(e) => setIsEntireClass(e.target.checked)}
                            className="mb-2"
                          />
                          <CustomInput
                            type="checkbox"
                            id="isEntireProgram"
                            label={(!specialty || specialty.value === 'pmr') ? "This is the entire program (e.g., all PGY years)" : "This is the entire program"}
                            checked={isEntireProgram}
                            onChange={(e) => handleEntireProgramChange(e.target.checked)}
                          />
                          <small className="text-muted d-block mt-2">
                            <i className="fas fa-info-circle mr-1" />
                            Minimum 10 seats or entire class required for group discount
                          </small>
                        </div>
                        <hr />
                        <Label className="font-weight-bold">Line Items <span className="text-danger">*</span></Label>
                        {items.map((item, idx) => {
                          const itemErr = errors.items?.[idx];
                          return (
                            <Row key={idx} className="mb-2 align-items-start">
                              <Col md="7">
                                <Select
                                  options={planOptions}
                                  value={item.plan ? planOptions.find((o) => o.value === item.plan.id) : null}
                                  onChange={(val) => updateItem(idx, 'plan', val?.plan || null)}
                                  styles={{
                                    ...customDropdownWithBorder,
                                    control: (base, state) => ({
                                      ...customDropdownWithBorder.control(base, state),
                                      ...(itemErr?.plan ? { borderColor: '#fb6340', '&:hover': { borderColor: '#fb6340' } } : {}),
                                    }),
                                  }}
                                  placeholder="Select plan..."
                                  isClearable
                                />
                                {itemErr?.plan && <small className="text-danger">{itemErr.plan}</small>}
                              </Col>
                              <Col md="3">
                                <Input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  invalid={!!itemErr?.quantity}
                                  onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                                  placeholder="Qty"
                                />
                                {itemErr?.quantity && <FormFeedback>{itemErr.quantity}</FormFeedback>}
                              </Col>
                              <Col md="2">
                                <Button
                                  color="danger"
                                  outline
                                  size="sm"
                                  className="mt-1"
                                  disabled={items.length <= 1}
                                  onClick={() => removeItem(idx)}
                                >
                                  <i className="fas fa-trash" />
                                </Button>
                              </Col>
                            </Row>
                          );
                        })}
                        <Button color="default" outline size="sm" className="mt-2" onClick={addItem}>
                          <i className="fas fa-plus mr-1" /> Add Plan
                        </Button>
                      </CardBody>
                    </Card>

                    {errors.submit && (
                      <div className="alert alert-danger">{errors.submit}</div>
                    )}
                  </Col>

                  {/* Order Summary Sidebar */}
                  <Col lg="4">
                    <Card className="border shadow-sm" style={{ position: 'sticky', top: '100px' }}>
                      <CardHeader className="bg-gradient-primary py-3">
                        <h5 className="mb-0 text-white">
                          <i className="fas fa-calculator mr-2" />
                          Order Summary
                        </h5>
                      </CardHeader>
                      <CardBody>
                        {itemSummaries.some((s) => s) ? (
                          <>
                            {/* Discount badge */}
                            {discountLabel && (
                              <div className="text-center mb-3">
                                <span
                                  className="badge badge-lg"
                                  style={{
                                    background: isEntireProgram ? 'linear-gradient(135deg, #5e72e4, #825ee4)' : 'linear-gradient(135deg, #2dce89, #2dcecc)',
                                    color: '#fff',
                                    padding: '6px 14px',
                                    fontSize: '0.85rem',
                                    borderRadius: '20px',
                                  }}
                                >
                                  <i className="fas fa-tag mr-1" />
                                  {discountLabel}
                                </span>
                              </div>
                            )}

                            {itemSummaries.map((s, idx) => s && (
                              <div key={idx} className="mb-2 pb-2" style={idx < itemSummaries.filter(Boolean).length - 1 ? { borderBottom: '1px dashed #e9ecef' } : {}}>
                                <div className="d-flex justify-content-between">
                                  <span className="font-weight-bold" style={{ fontSize: '0.85rem' }}>{s.plan.name}</span>
                                  <span style={{ fontSize: '0.85rem' }}>{s.quantity} seat{s.quantity !== 1 ? 's' : ''}</span>
                                </div>
                                <div className="d-flex justify-content-between text-muted" style={{ fontSize: '0.8rem' }}>
                                  <span>
                                    {discountPercent > 0 ? (
                                      <>
                                        <span style={{ textDecoration: 'line-through', marginRight: '6px' }}>${s.standardPrice}</span>
                                        <span style={{ color: '#2dce89', fontWeight: 600 }}>${s.discountedPrice}</span>
                                        <span>/seat</span>
                                      </>
                                    ) : (
                                      <>${s.standardPrice}/seat</>
                                    )}
                                  </span>
                                  <span>${s.subtotal}</span>
                                </div>
                              </div>
                            ))}

                            <div className="d-flex justify-content-between mb-2">
                              <span className="text-muted">Total Seats</span>
                              <span>{totalSeats}</span>
                            </div>

                            {/* Savings line */}
                            {totalSavings > 0 && (
                              <div className="d-flex justify-content-between mb-2" style={{ color: '#2dce89' }}>
                                <span className="font-weight-bold">You Save</span>
                                <span className="font-weight-bold">${totalSavings}</span>
                              </div>
                            )}

                            <hr />
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="h4 mb-0">Total</span>
                              <span className="h3 mb-0 text-success">${totalAmount}</span>
                            </div>

                            {/* Nudge toward discount */}
                            {seatsUntilDiscount > 0 && totalSeats > 0 && (
                              <div className="mt-3 p-2 text-center" style={{ background: '#fff8e1', borderRadius: '6px', fontSize: '0.8rem' }}>
                                <i className="fas fa-lightbulb mr-1 text-warning" />
                                Add {seatsUntilDiscount} more seat{seatsUntilDiscount !== 1 ? 's' : ''} to unlock <strong>20% group discount</strong>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-center text-muted py-3">
                            <i className="fas fa-info-circle mb-2" style={{ fontSize: '1.5rem' }} />
                            <p className="mb-0" style={{ fontSize: '0.85rem' }}>
                              Add plans and quantities to see pricing
                            </p>
                          </div>
                        )}

                        <Button
                          color="primary"
                          type="submit"
                          className="w-100 mt-4"
                          size="lg"
                          disabled={submitting}
                        >
                          {submitting ? (
                            <Spinner size="sm" />
                          ) : (
                            <>
                              <i className="fas fa-paper-plane mr-2" />
                              Submit Quote Request
                            </>
                          )}
                        </Button>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </form>
            )}
          </Container>
        </section>

        {/* Footer */}
        <footer className="pmr-footer">
          <Container>
            <Row>
              <Col lg="4" className="mb-4 mb-lg-0">
                <img
                  src={require('assets/img/brand/pmr-logo.png')}
                  alt={`${getSpecialty(specialty?.value).name} Prep`}
                  className="pmr-footer-logo"
                />
                <p className="pmr-footer-description">
                  The most comprehensive {getSpecialty(specialty?.value).name} board review question bank.
                  Built by {getSpecialty(specialty?.value).name} physicians.
                </p>
                <div className="pmr-social-links">
                  <a href="mailto:support@pmrprep.io" aria-label="Email">
                    <i className="fas fa-envelope" />
                  </a>
                </div>
              </Col>
              <Col lg="2" md="4" className="mb-4 mb-md-0">
                <h5 className="pmr-footer-title">Product</h5>
                <ul className="pmr-footer-links">
                  <li><Link to="/">Home</Link></li>
                  <li><Link to="/auth/register">Get Started</Link></li>
                </ul>
              </Col>
              <Col lg="2" md="4" className="mb-4 mb-md-0">
                <h5 className="pmr-footer-title">Resources</h5>
                <ul className="pmr-footer-links">
                  <li><Link to="/auth/register">Free Trial</Link></li>
                  <li><Link to="/auth/login">Sign In</Link></li>
                </ul>
              </Col>
              <Col lg="2" md="4">
                <h5 className="pmr-footer-title">Contact</h5>
                <ul className="pmr-footer-links pmr-footer-contact">
                  <li>
                    <i className="fas fa-envelope mr-2" />
                    support@pmrprep.io
                  </li>
                </ul>
              </Col>
            </Row>
            <hr className="pmr-footer-divider" />
            <Row>
              <Col className="text-center">
                <p className="pmr-copyright">
                  &copy; {new Date().getFullYear()} {getSpecialty(specialty?.value).name} Prep. All rights reserved.
                </p>
              </Col>
            </Row>
          </Container>
        </footer>
      </div>
    </>
  );
};

export default GroupPricing;
